import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Link, useNavigate } from "react-router-dom";

function FocusList() {
  const [focusAreas, setFocusAreas] = useState([]);
  const [showInactive, setShowInactive] = useState(false); // ✅ NEW
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const baseURL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("profileToken");
  const navigate = useNavigate();
  const headers = { "x-access-token": token || "" };

  const fetchFocusAreas = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await axios.get(`${baseURL}/focus/list`, { headers });

      if (res.data.status) {
        setFocusAreas(res.data.result || []);
      } else {
        setFocusAreas([]);
        setError(res.data.message || "Failed to fetch focus areas");
      }
    } catch (err) {
      console.error("Error fetching focus areas:", err);
      setError("Server error while fetching focus areas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFocusAreas();
    // eslint-disable-next-line
  }, []);

  // ✅ counts
  const activeCount = useMemo(
    () => focusAreas.filter((f) => Number(f.status) === 1).length,
    [focusAreas]
  );
  const inactiveCount = useMemo(
    () => focusAreas.filter((f) => Number(f.status) === 0).length,
    [focusAreas]
  );

  // ✅ visible list
  const visibleFocus = useMemo(() => {
    return focusAreas.filter((f) =>
      showInactive ? Number(f.status) === 0 : Number(f.status) === 1
    );
  }, [focusAreas, showInactive]);

  const handleToggleStatus = async (focus) => {
    try {
      await axios.patch(`${baseURL}/focus/${focus.id}/status`, {}, { headers });

      const newStatus = Number(focus.status) === 1 ? 0 : 1;

      setFocusAreas((prev) =>
        prev.map((x) => (x.id === focus.id ? { ...x, status: newStatus } : x))
      );

      // ✅ auto switch view
      setShowInactive(newStatus === 0);

      Swal.fire("Success", "Status updated", "success");
    } catch (err) {
      Swal.fire("Error!", "Failed to update status.", "error");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This focus area will be deleted permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await axios.delete(`${baseURL}/focus/delete/${id}`, { headers });

      if (res.data.status) {
        setFocusAreas((prev) => prev.filter((f) => f.id !== id));
        Swal.fire("Deleted!", "Focus area has been deleted.", "success");
      } else {
        Swal.fire("Error!", res.data.message || "Failed to delete focus area.", "error");
      }
    } catch (err) {
      console.error("Delete error:", err);
      Swal.fire("Error!", "Failed to delete focus area.", "error");
    }
  };

  if (loading)
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-success" role="status"></div>
        <p className="mt-3">Loading focus areas...</p>
      </div>
    );

  if (error)
    return (
      <div className="text-center text-danger my-5">
        <p>{error}</p>
      </div>
    );

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0 text-success">
          {showInactive ? "Inactive Focus Areas" : "Focus Areas List"}{" "}
          <span className="badge bg-secondary ms-2">
            {showInactive ? inactiveCount : activeCount}
          </span>
        </h3>

        <div className="d-flex gap-2">
          {/* ✅ Toggle Active/Inactive */}
          <button
            className={`btn ${
              showInactive ? "btn-outline-success" : "btn-outline-danger"
            } rounded-pill px-4`}
            onClick={() => setShowInactive((p) => !p)}
          >
            {showInactive
              ? `Active Focus Areas (${activeCount})`
              : `Inactive Focus Areas (${inactiveCount})`}
          </button>

          <Link className="btn btn-success rounded-pill px-4" to="/focus-add">
            <i className="fas fa-plus me-2"></i> Add Focus Area
          </Link>
        </div>
      </div>

      {visibleFocus.length === 0 ? (
        <p className="text-muted">
          {showInactive ? "No inactive focus areas available." : "No active focus areas available."}
        </p>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-bordered align-middle text-center">
            <thead className="table-success">
              <tr>
                <th>#</th>
                <th>Text</th>
                <th>Icon</th>
                <th>Image</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {visibleFocus.map((f, i) => (
                <tr
                  key={f.id}
                  className={Number(f.status) === 0 ? "table-secondary" : ""}
                  style={Number(f.status) === 0 ? { opacity: 0.9 } : {}}
                >
                  <td>{i + 1}</td>

                  <td>
                    {f.text}
                    {Number(f.status) === 0 && <span className="badge bg-danger ms-2">Inactive</span>}
                  </td>

                  <td>
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <i className={`fas ${f.icon}`} style={{ fontSize: 18 }} />
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        {f.icon}
                      </span>
                    </div>
                  </td>

                  <td>
                    <img
                      src={
                        f.image
                          ? `${baseURL}/uploads/${f.image}?t=${Date.now()}`
                          : "/default-user.png"
                      }
                      alt={f.text}
                      style={{
                        width: 70,
                        height: 50,
                        objectFit: "cover",
                        borderRadius: "6px",
                      }}
                    />
                  </td>

                  <td>
                    <div className="form-check form-switch d-flex justify-content-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={Number(f.status) === 1}
                        onChange={() => handleToggleStatus(f)}
                      />
                    </div>
                  </td>

                  <td>
                    <div className="d-flex justify-content-center gap-3">
                      <i
                        className="fas fa-edit text-warning"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/update-focus/${f.id}`)}
                        title="Edit"
                      ></i>

                      <i
                        className="fas fa-trash text-danger"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDelete(f.id)}
                        title="Delete"
                      ></i>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>
      )}
    </div>
  );
}

export default FocusList;
